import React, { FunctionComponent, useMemo, useRef, useState } from "react";
import { Box, Divider } from "@mui/material";

import {
  COLORS,
  SearchablePaper,
  SearchContext,
  SimplePaper,
  TYPOGRAPHY_COMMON_STYLES,
} from "components";
import { EsgAutocompleteField } from "./esg-auto-complete-field";
import {
  EsgMultiSelectControlledFieldProps,
  MultiSelectControlledOption,
} from "./types";

export const EsgMultiselectControlledField: FunctionComponent<
  EsgMultiSelectControlledFieldProps
> = ({
  label,
  selectedValues,
  options,
  setSelectedValues,
  disabled,
  isAllSelectedOnEmptySelection = false,
  color,
  message,
  type,
  allowSearchbar = false,
  onchangeFn,
  renderTagsFn,
}) => {
  const [searchText, setSearchText] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const shouldPreventCloseRef = useRef(false);

  const selectedCount = selectedValues?.length ?? 0;
  const totalOptions = options?.length ?? 0;

  const allSelected = selectedCount === totalOptions;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalOptions;

  const searchContextValue = useMemo(
    () => ({
      value: searchText,
      onChange: setSearchText,
      onContainerMouseDown: () => {
        shouldPreventCloseRef.current = true;
      },
    }),
    [searchText],
  );

  const computedOptions = useMemo(
    () => [
      { label: "Select All", value: "ALL", name: "Select All" },
      ...options,
    ],
    [options],
  );

  const filterOptions = useMemo(() => {
    if (!allowSearchbar) return undefined;

    return (opts: unknown[]) =>
      (opts as MultiSelectControlledOption[]).filter(
        (opt) =>
          opt.value === "ALL" ||
          (opt.label ?? "").toLowerCase().includes(searchText.toLowerCase()),
      );
  }, [allowSearchbar, searchText]);

  const handleSelectAll = (checked: boolean) => {
    setSelectedValues(checked ? options : []);
  };

  const handleOptionToggle = (
    option: MultiSelectControlledOption,
    isSelected: boolean,
    checked: boolean,
  ) => {
    onchangeFn?.(option.value, isSelected);

    setSelectedValues(
      checked
        ? [...selectedValues, option]
        : selectedValues.filter((s) => s.value !== option.value),
    );
  };

  const renderOption = (props: any, option: any) => {
    const isSelected = selectedValues.some(
      (selected) => selected.value === option.value,
    );

    const commonStyle = {
      color: COLORS.Romance,
      fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
      fontSize: 14,
      lineHeight: "18px",
      backgroundColor: isSelected ? "#F6F8FA" : "white",
      display: "flex",
      alignItems: "center",
    };

    if (option.value === "ALL") {
      return (
        <>
          <li {...props} key={option.value} style={commonStyle}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = isIndeterminate; // eslint-disable-line no-param-reassign
              }}
              onClick={(e) => {
                e.stopPropagation();
                onchangeFn?.(option.value);
              }}
              onChange={(e) => handleSelectAll(e.target.checked)}
              style={{
                marginRight: 10,
                cursor: "pointer",
                accentColor: "#054254",
              }}
            />
            Select All
          </li>
          <Divider />
        </>
      );
    }

    return (
      <li {...props} key={option.value} style={commonStyle}>
        <input
          type="checkbox"
          checked={isSelected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            handleOptionToggle(option, isSelected, e.target.checked);
          }}
          style={{
            marginRight: 10,
            cursor: "pointer",
            accentColor: "#054254",
          }}
        />
        {option.name}
      </li>
    );
  };

  const content = (
    <EsgAutocompleteField
      type={type}
      textFieldProps={{
        label,
        disabled,
        placeholder:
          isAllSelectedOnEmptySelection && selectedCount === 0
            ? "All Selected"
            : selectedCount === 0
            ? "No selection"
            : selectedCount > 0 && type === "checkMissing"
            ? ""
            : undefined,
        message,
        color,
      }}
      autocompleteProps={{
        disabled,
        multiple: true,
        value: selectedValues,
        disableClearable: true,
        blurOnSelect: false,
        disableCloseOnSelect: true,

        open: dropdownOpen,
        onOpen: () => setDropdownOpen(true),
        onClose: () => {
          if (shouldPreventCloseRef.current) {
            shouldPreventCloseRef.current = false;
            return;
          }
          setDropdownOpen(false);
          setSearchText("");
        },

        PaperComponent: allowSearchbar ? SearchablePaper : SimplePaper,

        filterOptions,

        onChange: () => {},

        options: computedOptions,

        isOptionEqualToValue: (opt, val) =>
          (opt as MultiSelectControlledOption).value ===
          (val as MultiSelectControlledOption).value,

        renderTags: renderTagsFn
          ? (value) => renderTagsFn(value as MultiSelectControlledOption[])
          : (value) => (
              <Box sx={{ ml: 1 }} component="span">
                {value.length === 1
                  ? `${value.length} selected`
                  : value.length === totalOptions
                  ? "All Selected"
                  : `${value.length} selected`}
              </Box>
            ),

        renderOption,
      }}
    />
  );

  return allowSearchbar ? (
    <SearchContext.Provider value={searchContextValue}>
      {content}
    </SearchContext.Provider>
  ) : (
    content
  );
};
